import { activeJobStore } from '../../active-job/active-job-state.js';
import { getRenderableFeaturesFromGeohash, getBoundsFromGeohash } from './JobGeohashFeatures.js';
import { setJobEntities, removeJobEntities, flyToJobBounds, onMapRegistered } from '../map-interaction/map-interaction-service.js';

/** @import { ActiveJobState } from '../../active-job/active-job-state.js' */

/** @type {?() => void} Unsubscribe function for the active job store subscription, null while off. */
let unsubscribeFromActiveJobStore = null;

/** @type {?() => void} Unsubscribe function for the map-registered subscription, null while off. */
let unsubscribeFromMapRegistered = null;

/**
 * Syncs the active job's geohash to every registered map: renders the job's features when
 * it loads or its geohash changes, flies the main map to it on first load, and clears the previous
 * job's features when a different job becomes active.
 *
 * Also primes future map registrations (flies to features when switching google/Cesium map modes).
 *
 * Only the first call subscribes, safe to call repeatedly.
 */
export function init() {
  if (unsubscribeFromActiveJobStore != null) return;

  // Render onto whatever is already registered. Safe to call with nothing registered yet.
  renderActiveJob();
  unsubscribeFromActiveJobStore = activeJobStore.subscribe(activeJobStateChanged);
  unsubscribeFromMapRegistered = onMapRegistered(primeNewMap);
}

/**
 * Stops syncing the active job to the map(s). Safe to call repeatedly.
 */
export function teardown() {
  if (unsubscribeFromActiveJobStore == null) return;

  unsubscribeFromActiveJobStore();
  unsubscribeFromActiveJobStore = null;
  unsubscribeFromMapRegistered?.();
  unsubscribeFromMapRegistered = null;
}

/** Renders the currently active job's features on every registered map, if a job is active. */
function renderActiveJob() {
  const { id, geohash } = activeJobStore.getState();
  if (id == null || geohash == null) return;

  const features = getRenderableFeaturesFromGeohash(id, geohash);
  setJobEntities(id, features).catch((error) => {
    console.error('Error rendering job on map:', error);
  });
}

/**
 * Primes a map that registers after the active job was already loaded - imitates a fresh load when switching between Cesium and Google Maps.
 */
function primeNewMap() {
  renderActiveJob();

  try {
    const { geohash } = activeJobStore.getState();
    const bounds = geohash != null ? getBoundsFromGeohash(geohash) : null;
    if (bounds != null) flyToJobBounds(bounds);
  } catch (error) {
    console.error('Error flying to a newly registered map:', error);
  }
}

/**
 * @param {ActiveJobState} state
 * @param {ActiveJobState} prevState
 */
function activeJobStateChanged(state, prevState) {
  try {
    const jobChanged = prevState.id != state.id;
    if (jobChanged && prevState.id != null) {
      removeJobEntities(prevState.id);
    }

    renderActiveJob();

    const geohashFirstLoaded = prevState.geohash == null && state.geohash != null;
    if (geohashFirstLoaded) {
      const bounds = getBoundsFromGeohash(state.geohash);
      if (bounds != null) flyToJobBounds(bounds);
    }
  } catch (error) {
    console.error('Error in active job store subscription callback:', error);
  }
}
